import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronRight, Store } from 'lucide-react'
import { type CoachStore, listCoachStores } from '@/api/coach'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'

export const Route = createFileRoute('/coach/stores/')({
  component: CoachStoresPage,
})

function CoachStoresPage() {
  const query = useQuery({
    queryKey: ['coach', 'stores'],
    queryFn: listCoachStores,
  })

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6 pb-12">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Мои магазины</h1>
        <p className="text-sm text-muted-foreground">
          Откройте магазин, чтобы посмотреть сводку по продавцам за период.
        </p>
      </header>

      {query.isLoading ? (
        <StatusCard title="Загружаем магазины" description="Это займёт секунду." />
      ) : query.isError ? (
        <StatusCard
          title="Не удалось загрузить магазины"
          description={describeErrorMessage(query.error, 'Попробуйте обновить.')}
        />
      ) : query.data && query.data.length === 0 ? (
        <StatusCard
          title="К вам ещё не привязаны магазины"
          description="Попросите администратора добавить вас к нужному магазину."
        />
      ) : (
        <ul className="flex flex-col gap-2" aria-label="Список магазинов">
          {query.data?.map((store) => (
            <StoreRow key={store.id} store={store} />
          ))}
        </ul>
      )}
    </main>
  )
}

interface StoreRowProps {
  store: CoachStore
}

function StoreRow({ store }: StoreRowProps) {
  return (
    <li>
      <Link
        to="/coach/stores/$id"
        params={{ id: store.id }}
        className="group flex items-center gap-3 rounded-lg border bg-card px-4 py-3 transition-colors hover:border-primary/40 hover:bg-accent"
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Store className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="flex flex-1 flex-col">
          <span className="font-medium text-foreground">{store.name}</span>
          <span className="text-xs text-muted-foreground">{store.city}</span>
        </span>
        <ChevronRight
          className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </Link>
    </li>
  )
}

interface StatusCardProps {
  title: string
  description: string
}

function StatusCard({ title, description }: StatusCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        <p>Если всё выглядит правильно, обновите страницу.</p>
      </CardContent>
    </Card>
  )
}
